import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from '@components/Modal';
import I18n, { translate } from '@utils/i18n';
import { DropDownList } from '@progress/kendo-react-dropdowns';

const namespace = 'features.settings.price_lists.index.action_modal';
const minHeight = 350;

function ActionModal({ visible, onCloseModal, priceList, priceListForSelect, associatedItems, onConfirm }) {
  const [replacePriceListId, setReplacePriceListId] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [formHeight, setFormHeight] = useState(minHeight);

  useEffect(() => {
    const defaultPriceList = priceListForSelect.find((list) => list.is_default);
    if (defaultPriceList?.id !== priceList?.id) {
      setReplacePriceListId(defaultPriceList?.id);
    } else {
      setReplacePriceListId(null);
    }
  }, [priceList]);

  useEffect(() => {
    if (associatedItems) {
      const itemsHeight = Object.values(associatedItems).filter((item) => item > 0).length * 34;
      const footerHeight = Object.values(associatedItems).some((item) => item > 0) ? 50 : 0;
      setFormHeight(minHeight + itemsHeight + footerHeight);
    }
  }, [associatedItems]);

  const priceLists = useMemo(
    () =>
      priceListForSelect
        .filter((list) => list.id !== priceList?.id)
        .map((list) => {
          return {
            label: list.is_default ? `${list.name} (Default)` : list.name,
            value: list.id
          };
        }),
    [priceListForSelect, priceList]
  );

  const ddListValue = () => {
    if (!replacePriceListId) return null;

    return priceLists.find((list) => list.value === replacePriceListId) || null;
  };

  const hasRequiredItems = useMemo(() => {
    return associatedItems && Object.values(associatedItems).some((item) => item > 0);
  }, [associatedItems]);

  const isFormValid = useMemo(() => {
    if (!hasRequiredItems) return true;
    return replacePriceListId !== null;
  }, [hasRequiredItems, replacePriceListId]);

  const onFilterChange = (event) => {
    setFilterQuery(event.filter.value);
  };

  const filteredPriceLists = useMemo(() => {
    if (filterQuery === '') return priceLists;

    const re = new RegExp(filterQuery, 'i');

    return priceLists.filter((list) => re.test(list.label));
  }, [filterQuery, priceLists]);

  const onChange = (e) => {
    setReplacePriceListId(e.target.value.value);
  };

  if (!visible || !associatedItems) return null;

  return (
    <Modal
      title=<h4>
        <strong>{translate('dialog_title', { namespace, price_list: priceList.name })}</strong>
      </h4>
      className="qmb-dialog"
      width={600}
      height={formHeight}
      isOpen={visible}
      onClose={onCloseModal}>
      {priceList?.is_default && hasRequiredItems && (
        <p>{translate('note_default_price_list_with_pending_items', { namespace })}</p>
      )}
      {priceList?.is_default && !hasRequiredItems && (
        <>
          <p>{translate('note_default_price_list_without_pending_items', { namespace })}</p>
          <p>{translate('subnote_default_price_list_without_pending_items', { namespace })}</p>
        </>
      )}
      {!priceList?.is_default && (
        <>
          <p>{translate('note', { namespace })}</p>
          <p>{translate('subnote', { namespace })}</p>
        </>
      )}

      <br />

      {associatedItems &&
        Object.entries(associatedItems).map(([key, value]) => {
          if (value > 0) {
            return (
              <p key={key}>
                {value} {translate(key, { namespace })}
              </p>
            );
          }
          return null;
        })}

      <br />

      {hasRequiredItems && (
        <fieldset style={{ width: '100%' }}>
          <div className="qmb-select--blank" style={{ width: '300px' }}>
            <DropDownList
              filterable
              onFilterChange={onFilterChange}
              id="priceListSelector"
              className="qmb-popup"
              textField="label"
              dataItemKey="value"
              data={filteredPriceLists}
              onChange={onChange}
              defaultItem={{
                label: translate('select_replacement_price_list_placeholder', { namespace }),
                value: ''
              }}
              value={ddListValue()}
            />
          </div>
        </fieldset>
      )}

      {hasRequiredItems && (
        <>
          <br />
          <br />
          <p style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
            {translate('footer_note_for_pending_items', { namespace })}
          </p>
        </>
      )}

      <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
        <button type="button" className="qmb-button" onClick={onCloseModal}>
          {I18n.t('generic.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onConfirm(replacePriceListId)}
          className="qmb-button--submit"
          disabled={!isFormValid}>
          {I18n.t('generic.confirm')}
        </button>
      </div>
    </Modal>
  );
}

ActionModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  associatedItems: PropTypes.object,
  priceListForSelect: PropTypes.array.isRequired,
  priceList: PropTypes.object
};

ActionModal.defaultProps = {
  priceList: {
    id: null,
    name: ''
  },
  associatedItems: null
};

export default ActionModal;
